import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Calendar, MapPin, Phone, Tag, List, Map as MapI, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import MapPicker from "@/components/admin/MapPicker";

const icon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function FitBoundsToMarkers({ items }) {
  const map = useMap();
  useEffect(() => {
    if (!items || items.length === 0) return;
    if (items.length === 1) {
      map.flyTo([items[0].lat, items[0].lng], 14, { duration: 0.6 });
    } else {
      const bounds = L.latLngBounds(items.map((e) => [e.lat, e.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [items, map]);
  return null;
}

export default function EventsList() {
  const { selected } = useProvince();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [view, setView] = useState("calendar"); // calendar | list
  const [selectedDate, setSelectedDate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", start_at: "", venue: "", address: "", contact_name: "", contact_phone: "", event_type: "", lat: null, lng: null });

  useEffect(() => {
    api.get(`/events?limit=200${selected ? `&province_id=${selected.id}` : ""}`).then((r) => setItems(r.data || []));
  }, [selected?.id]);

  const week = useMemo(() => {
    const days = [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const d = new Date(today); d.setDate(today.getDate() + i);
      days.push(d);
    }
    return days;
  }, []);

  const dayKey = (d) => d.toISOString().slice(0, 10);
  const filtered = selectedDate ? items.filter((e) => (e.start_at || "").slice(0, 10) === selectedDate) : items;
  const mapItems = filtered.filter((e) => e.lat && e.lng);
  const center = mapItems[0] ? [mapItems[0].lat, mapItems[0].lng] : [39.0, 35.0];

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        province_id: selected?.id,
        start_at: new Date(form.start_at).toISOString(),
        lat: form.lat != null ? Number(form.lat) : null,
        lng: form.lng != null ? Number(form.lng) : null,
      };
      await api.post("/events/submit", payload);
      toast.success("Etkinlik bildiriminiz alındı, onaydan sonra yayınlanacak");
      setShowForm(false);
      setForm({ title: "", description: "", start_at: "", venue: "", address: "", contact_name: "", contact_phone: "", event_type: "", lat: null, lng: null });
    } catch (e) { toast.error("Gönderilemedi"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black">Etkinlikler</h1>
          <p className="text-gray-500 text-sm">{selected?.name || "Türkiye"} - haftanın etkinlikleri</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setView("calendar")} className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1 ${view === "calendar" ? "bg-[#e11d2a] text-white" : "border border-gray-200"}`}><MapI className="w-4 h-4" /> Harita</button>
          <button onClick={() => setView("list")} className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1 ${view === "list" ? "bg-[#e11d2a] text-white" : "border border-gray-200"}`}><List className="w-4 h-4" /> Liste</button>
          <button onClick={() => setShowForm(!showForm)} className="px-3 py-1.5 text-sm rounded-md bg-[#0b0b0d] text-white flex items-center gap-1"><Plus className="w-4 h-4" /> Etkinlik Bildir</button>
        </div>
      </div>

      {/* Week pills */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-4">
        <button onClick={() => setSelectedDate(null)} className={`day-pill px-3 py-2 rounded-md text-sm border ${selectedDate === null ? "bg-[#e11d2a] text-white border-[#e11d2a]" : "border-gray-200"}`}>Tümü</button>
        {week.map((d) => (
          <button key={dayKey(d)} onClick={() => setSelectedDate(dayKey(d))} className={`day-pill min-w-[72px] px-3 py-2 rounded-md text-sm border text-center ${selectedDate === dayKey(d) ? "bg-[#e11d2a] text-white border-[#e11d2a]" : "border-gray-200"}`}>
            <div className="text-[10px] uppercase">{d.toLocaleString("tr-TR", { weekday: "short" })}</div>
            <div className="text-base font-bold">{d.getDate()}</div>
          </button>
        ))}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-gray-100 rounded-lg p-5 mb-5 grid md:grid-cols-2 gap-3">
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Etkinlik adı" className="px-3 py-2 border border-gray-200 rounded-md text-sm md:col-span-2" />
          <input value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} placeholder="Etkinlik türü" className="px-3 py-2 border border-gray-200 rounded-md text-sm" />
          <input type="datetime-local" required value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="px-3 py-2 border border-gray-200 rounded-md text-sm" />
          <input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} placeholder="Mekan" className="px-3 py-2 border border-gray-200 rounded-md text-sm" />
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adres" className="px-3 py-2 border border-gray-200 rounded-md text-sm" />
          <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="İrtibat" className="px-3 py-2 border border-gray-200 rounded-md text-sm" />
          <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="Telefon" className="px-3 py-2 border border-gray-200 rounded-md text-sm" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Açıklama" rows={3} className="px-3 py-2 border border-gray-200 rounded-md text-sm md:col-span-2"></textarea>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Konum (haritaya tıklayarak pin yerleştirin)
            </label>
            <MapPicker
              lat={form.lat}
              lng={form.lng}
              onChange={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))}
              height={280}
              defaultQuery={form.address || form.venue || selected?.name || ""}
            />
            <p className="text-[11px] text-gray-500 mt-1">İsteğe bağlı. Pin koyarsanız etkinlik haritada görüntülenir.</p>
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-md">İptal</button>
            <button type="submit" data-testid="event-submit-btn" className="px-4 py-2 text-sm bg-[#e11d2a] text-white rounded-md">Gönder</button>
          </div>
        </form>
      )}

      {view === "calendar" && mapItems.length > 0 && (
        <div className="h-96 rounded-lg overflow-hidden mb-5 border border-gray-100">
          <MapContainer center={center} zoom={6} scrollWheelZoom={false} className="w-full h-full">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBoundsToMarkers items={mapItems} />
            {mapItems.map((e) => (
              <Marker key={e.id} position={[e.lat, e.lng]} icon={icon}>
                <Popup>
                  <div className="text-sm">
                    <div className="font-bold">{e.title}</div>
                    <div className="text-xs text-gray-600">{new Date(e.start_at).toLocaleString("tr-TR")}</div>
                    <div className="text-xs">{e.venue}</div>
                    <Link to={`/etkinlikler/${e.id}`} className="text-xs text-[#e11d2a] font-semibold">Detay →</Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((e) => (
          <Link key={e.id} to={`/etkinlikler/${e.id}`} className="news-card bg-white border border-gray-100 rounded-lg overflow-hidden">
            {e.cover_image && <img src={e.cover_image} alt={e.title} className="aspect-[16/9] w-full object-cover" />}
            <div className="p-4">
              {e.event_type && <div className="text-[10px] uppercase font-bold text-[#e11d2a] mb-1">{e.event_type}</div>}
              <h3 className="font-bold line-clamp-2">{e.title}</h3>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(e.start_at).toLocaleString("tr-TR")}</span>
                {e.venue && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.venue}</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function EventDetail() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  useEffect(() => { api.get(`/events/${id}`).then((r) => setE(r.data)); }, [id]);
  if (!e) return null;
  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {e.cover_image && <img src={e.cover_image} alt={e.title} className="w-full aspect-[16/9] object-cover rounded-lg" />}
      <h1 className="text-2xl md:text-3xl font-black mt-4">{e.title}</h1>
      <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
        <span className="flex items-center gap-1"><Calendar className="w-4 h-4 text-[#e11d2a]" />{new Date(e.start_at).toLocaleString("tr-TR")}</span>
        {e.venue && <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-[#e11d2a]" />{e.venue}</span>}
        {e.contact_phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4 text-[#e11d2a]" />{e.contact_phone}</span>}
        {e.event_type && <span className="flex items-center gap-1"><Tag className="w-4 h-4 text-[#e11d2a]" />{e.event_type}</span>}
      </div>
      <p className="mt-4 whitespace-pre-wrap">{e.description}</p>
      {e.lat && e.lng && (
        <div className="h-72 mt-4 rounded-lg overflow-hidden border">
          <MapContainer center={[e.lat, e.lng]} zoom={14} scrollWheelZoom={false} className="w-full h-full">
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[e.lat, e.lng]} icon={icon}><Popup>{e.title}</Popup></Marker>
          </MapContainer>
        </div>
      )}
    </div>
  );
}
